import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="website_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      return;
    }

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9p0fi8senbniolesnc0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        if (msg.toLowerCase().includes('spam') || msg.includes('form data is spam')) {
          setFormError('提交失败，请稍后重试');
        } else {
          setFormError(msg);
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于韩漫画网',
    description: '韩漫画网品牌介绍 - 无遮韩国免费漫画大全，致力于为韩漫爱好者提供纯净无广告的免费在线阅读体验。',
    url: 'https://www.hanmanhua.com/about',
  };

  return (
    <>
      <SEOHead
        title="关于我们 | 品牌介绍与联系方式"
        description="了解韩漫画网——无遮韩国免费漫画大全。我们致力于为韩漫爱好者提供纯净无广告的免费在线阅读体验。查看品牌故事、用户口碑和联系方式。"
        keywords="韩漫画网,关于我们,韩漫平台,免费漫画,联系方式"
        canonicalUrl="https://www.hanmanhua.com/about"
        structuredData={structuredData}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />

          {/* Hero */}
          <section className="py-10 md:py-14 text-center">
            <h1 className="text-3xl md:text-4xl font-bold font-heading text-foreground-900 mb-4">
              关于韩漫画网
            </h1>
            <p className="text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
              无遮韩国免费漫画大全 —— 您的专属韩漫阅读平台
            </p>
          </section>

          {/* Brand Introduction */}
          <section className="py-10 md:py-14 border-t border-background-200">
            <div className="max-w-4xl mx-auto">
              <div className="flex flex-col md:flex-row gap-8 md:gap-12 items-center">
                <div className="flex-1">
                  <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-4">
                    品牌介绍
                  </h2>
                  <div className="space-y-3 text-sm text-foreground-600 leading-relaxed">
                    <p>
                      韩漫画网成立于2024年，是一个专注于<strong>韩国漫画（韩漫）</strong>资源分享的在线阅读平台。我们致力于为广大华语韩漫爱好者打造一个<strong>纯净、无广告、高品质</strong>的漫画阅读空间。
                    </p>
                    <p>
                      我们的团队由一群热爱漫画文化的年轻人组成。我们深知在阅读过程中被广告打断是多么令人烦恼的体验，因此韩漫画网承诺：<strong>阅读环境完全纯净无广告</strong>，让您能够全心全意沉浸在精彩的韩国漫画世界中。
                    </p>
                    <p>
                      目前，韩漫画网已收录了涵盖恋爱、奇幻、动作、悬疑、喜剧、惊悚、日常、古风等多个分类的海量韩漫资源。无论是热门的连载作品，还是经典的高分佳作，您都能在这里找到。
                    </p>
                    <p>
                      我们坚持<strong>每日更新</strong>，确保您能在第一时间阅读到喜爱漫画的最新章节。韩漫画网将持续优化阅读体验，丰富漫画资源库，成为华语地区最值得信赖的韩漫阅读平台。
                    </p>
                  </div>
                </div>
                <div className="w-full md:w-[320px] flex-shrink-0">
                  <div className="aspect-square rounded-lg overflow-hidden">
                    <img
                      src="https://readdy.ai/api/search-image?query=Warm%20minimalist%20illustration%20of%20a%20cozy%20reading%20nook%20with%20books%20and%20soft%20lighting%2C%20coral%20and%20amber%20accent%20colors%2C%20peaceful%20atmosphere%2C%20Korean%20aesthetic%2C%20simple%20composition&width=800&height=800&seq=about-brand-01&orientation=squarish"
                      alt="韩漫画网品牌形象"
                      title="韩漫画网 - 舒适阅读空间"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Stats */}
          <section className="py-10 md:py-14 border-t border-background-200">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 text-center mb-8">
                我们的数据
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                {[
                  { value: '500+', label: '漫画作品', icon: 'ri-book-2-line' },
                  { value: '12', label: '内容分类', icon: 'ri-stack-line' },
                  { value: '50万+', label: '月活用户', icon: 'ri-user-heart-line' },
                  { value: '99.9%', label: '用户好评', icon: 'ri-thumb-up-line' },
                ].map((stat, idx) => (
                  <div
                    key={idx}
                    className="text-center p-5 rounded-lg bg-background-100"
                  >
                    <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-accent-100 flex items-center justify-center">
                      <i className={`${stat.icon} text-accent-600 text-lg`}></i>
                    </div>
                    <p className="text-2xl md:text-3xl font-bold font-heading text-accent-600">
                      {stat.value}
                    </p>
                    <p className="text-xs text-foreground-500 mt-1">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Reviews */}
          <section className="py-10 md:py-14 border-t border-background-200">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 text-center mb-8">
                用户口碑
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                {[
                  {
                    name: '漫画迷小王',
                    text: '用了很多漫画网站，韩漫画网是最干净的！完全没有广告弹窗，阅读体验一流。每天都来这里追更新，强烈推荐！',
                    rating: 5,
                  },
                  {
                    name: '韩漫爱好者阿琳',
                    text: '资源很全，更新也快，想看的热门韩漫基本都有。界面设计也很好看，用手机看漫画非常流畅，五星好评！',
                    rating: 5,
                  },
                  {
                    name: '读书人老张',
                    text: '分类很清晰，搜索也很方便。特别喜欢他们的推荐板块，帮我发现了好多好看的冷门韩漫。纯净无广告这点真的加分。',
                    rating: 5,
                  },
                ].map((review, idx) => (
                  <div
                    key={idx}
                    className="p-5 rounded-lg bg-background-100"
                  >
                    <div className="flex items-center gap-0.5 mb-3">
                      {Array.from({ length: review.rating }).map((_, i) => (
                        <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                      "{review.text}"
                    </p>
                    <p className="text-xs font-medium text-foreground-800">
                      —— {review.name}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact */}
          <section className="py-10 md:py-14 border-t border-background-200">
            <div className="max-w-2xl mx-auto">
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 text-center mb-2">
                联系我们
              </h2>
              <p className="text-sm text-foreground-500 text-center mb-8">
                如果您有任何问题、意见或合作意向，欢迎通过以下方式联系我们
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
                <div className="text-center p-5 rounded-lg bg-background-100">
                  <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-primary-100 flex items-center justify-center">
                    <i className="ri-mail-line text-primary-600 text-lg"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h3>
                  <p className="text-xs text-foreground-500">contact@hanmanhua.com</p>
                </div>
                <div className="text-center p-5 rounded-lg bg-background-100">
                  <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-accent-100 flex items-center justify-center">
                    <i className="ri-wechat-line text-accent-600 text-lg"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1">微信公众号</h3>
                  <p className="text-xs text-foreground-500">韩漫画网官方</p>
                </div>
                <div className="text-center p-5 rounded-lg bg-background-100">
                  <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-secondary-100 flex items-center justify-center">
                    <i className="ri-time-line text-secondary-600 text-lg"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1">工作时间</h3>
                  <p className="text-xs text-foreground-500">周一至周五 9:00-18:00</p>
                </div>
              </div>

              {/* Contact Form */}
              <form
                id="contact-form"
                data-readdy-form=""
                onSubmit={handleSubmit}
                className="bg-background-100 rounded-lg p-6"
              >
                <h3 className="text-base font-semibold text-foreground-900 mb-4">发送消息</h3>

                {formStatus === 'success' && (
                  <div className="mb-4 p-3 rounded-md bg-accent-100 text-accent-700 text-sm">
                    感谢您的留言！我们会尽快回复您。
                  </div>
                )}
                {formStatus === 'error' && (
                  <div className="mb-4 p-3 rounded-md bg-primary-100 text-primary-700 text-sm">
                    {formError}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700 mb-1.5">
                      姓名
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700 mb-1.5">
                      邮箱
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label htmlFor="contact-subject" className="block text-xs font-medium text-foreground-700 mb-1.5">
                    主题
                  </label>
                  <input
                    id="contact-subject"
                    name="subject"
                    type="text"
                    required
                    className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                    placeholder="请输入消息主题"
                  />
                </div>

                <div className="mb-4">
                  <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700 mb-1.5">
                    留言内容
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all resize-none"
                    placeholder="请输入您的留言内容（不超过500字）"
                  ></textarea>
                  <p className="text-right text-xs text-foreground-400 mt-1">最多500字</p>
                </div>

                {/* Honeypot */}
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="honeypot-field"
                />

                <button
                  type="submit"
                  className="w-full px-6 py-2.5 bg-primary-500 text-white text-sm font-medium rounded-full hover:bg-primary-600 transition-all duration-300 whitespace-nowrap"
                >
                  发送消息
                </button>
              </form>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </>
  );
}